import { AnimatePresence } from 'motion/react';
import { Loader2 } from 'lucide-react';
import { AnimatedListItem, Button } from '@/components/ui';
import ActivityCard from '../../pages/activity/ActivityCard';
import type { ActivityItem } from '../../types';
import { cn } from '@/lib/cn';

interface ActivityFeedProps {
  activities: ActivityItem[];
  realtimeCount: number;
  isLoading: boolean;
  emptyMessage?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  className?: string;
}

export function ActivityFeed({
  activities,
  realtimeCount,
  isLoading,
  emptyMessage = 'No activity yet',
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  className,
}: ActivityFeedProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <AnimatePresence initial={false}>
        {activities.map((activity, idx) => {
          const isRealtime = idx < realtimeCount;
          const card = (
            <ActivityCard
              key={activity.activityId}
              activity={activity}
              isRealtime={isRealtime}
            />
          );
          if (isRealtime) {
            return (
              <AnimatedListItem key={activity.activityId}>{card}</AnimatedListItem>
            );
          }
          return card;
        })}
      </AnimatePresence>

      {hasMore && onLoadMore && (
        <div className="flex justify-center py-4">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
